import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Checkbox from "../../../components/Checkbox/Checkbox";
import Form from "../../../components/Form/Form";
import FormTitle from "../../../components/FormTitle/FormTitle";
import Input from "../../../components/Input/Input";
import Select from "../../../components/Select/Select";
import { useUserStore } from "../../../contexts/userStore";
import AccessGroupService from "../../../services/AccessGroupService";
import "./FormAccessGroups.css";
import { LuUsers } from "react-icons/lu";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { NotificationManager } from "react-notifications";
import { GroupAccessModulesDDL } from "../../../components/GroupAccessModulesDDL/GroupAccessModulesDDL";

function FormAccessGroups(props) {
	const [name, setName] = useState(props.accessGroup?.name);
	const [startHour, setStartHour] = useState(props.accessGroup?.start_hour);
	const [endHour, setEndHour] = useState(props.accessGroup?.end_hour);
	const [startDate, setStartDate] = useState(props.accessGroup?.start_date);
	const [endDate, setEndDate] = useState(props.accessGroup?.end_date);
	const [description, setDescription] = useState(
		props.accessGroup?.description
	);
	const [sunday, setSundayEnabled] = useState(
		props.accessGroup?.days_of_week_activated?.[0] || false
	);
	const [mondayEnabled, setMondayEnabled] = useState(
		props.accessGroup?.days_of_week_activated?.[1] || false
	);
	const [tuesdayEnabled, setTuesdayEnabled] = useState(
		props.accessGroup?.days_of_week_activated?.[2] || false
	);
	const [wednesdayEnabled, setWednesdayEnabled] = useState(
		props.accessGroup?.days_of_week_activated?.[3] || false
	);
	const [thursdayEnabled, setThursdayEnabled] = useState(
		props.accessGroup?.days_of_week_activated?.[4] || false
	);
	const [friday, setFridayEnabled] = useState(
		props.accessGroup?.days_of_week_activated?.[5] || false
	);
	const [saturdayEnabled, setSaturdayEnabled] = useState(
		props.accessGroup?.days_of_week_activated?.[6] || false
	);
	const [continuousInterval, setContinuousInterval] = useState(
		props.accessGroup?.continuous_interval
	);
	const [keepUser, setKeepUser] = useState(
		props.accessGroup?.keep_user
	);
	const [direction, setDirection] = useState(props.accessGroup?.direction);
	const { selectedCondominio, selectedLicenceObj } = useUserStore();
	const [accessSelected, setAccessSelected] = useState(
		props.accessGroup?.accessModules
	);
	const [useAccessGroupsCheckbox, setUseAccessGroupsCheckbox] = useState(
		props.accessGroup?.useAccessGroupsEnabled || false
	);

	// Em modo isTemp, o filho é a fonte de verdade: só inicializa uma vez (montagem)
	// para não criar um loop filho→pai→filho quando o pai devolve o tempGroup atualizado.
	const tempInitializedRef = useRef(false);
	// Referência estável do accessGroup inicial — evita que o DualList receba um novo
	// objeto a cada notifyTempGroup e re-dispare onChangeList causando loop.
	const initialAccessGroupRef = useRef(props.accessGroup);

	useEffect(() => {
		if (props.isTemp) {
			if (tempInitializedRef.current) return; // bloqueia re-sync após a inicialização
			tempInitializedRef.current = true;
		}
		setName(props.accessGroup?.name);
		setStartHour(props.accessGroup?.start_hour);
		setEndHour(props.accessGroup?.end_hour);
		setStartDate(props.accessGroup?.start_date);
		setEndDate(props.accessGroup?.end_date);
		setSundayEnabled(props.accessGroup?.days_of_week_activated?.[0] || false);
		setMondayEnabled(props.accessGroup?.days_of_week_activated?.[1] || false);
		setTuesdayEnabled(props.accessGroup?.days_of_week_activated?.[2] || false);
		setWednesdayEnabled(props.accessGroup?.days_of_week_activated?.[3] || false);
		setThursdayEnabled(props.accessGroup?.days_of_week_activated?.[4] || false);
		setFridayEnabled(props.accessGroup?.days_of_week_activated?.[5] || false);
		setSaturdayEnabled(props.accessGroup?.days_of_week_activated?.[6] || false);
		setContinuousInterval(props.accessGroup?.continuous_interval || false);
		setKeepUser(props.accessGroup?.keep_user || false);
		setDirection(props.accessGroup?.direction);
		setUseAccessGroupsCheckbox(
			props.accessGroup?.useAccessGroupsEnabled || false
		);
	}, [props.accessGroup]);

	// useEffect(() => {
	// 	if (props.isTemp) {
	// 		let payload = buildPayload();
	// 		payload.name = payload.uuid;
	// 		delete payload.condoUsers;
	// 		const array = [];
	// 		array.push(payload);
	// 		props.handleNewTempGroup(array);
	// 	}
	// }, [props.tabChanged]);

	const navigate = useNavigate();

	// Notifica o pai (via onTempGroupChange) sempre que um campo é alterado no modo temp.
	// Recebe os valores atuais como parâmetros para evitar stale closures e loops de renderização.
	const notifyTempGroup = useCallback((
		{ _name, _startHour, _endHour, _startDate, _endDate, _description,
			_mondayEnabled, _tuesdayEnabled, _wednesdayEnabled, _thursdayEnabled,
			_friday, _saturdayEnabled, _sunday, _continuousInterval, _keepUser, _direction,
			_accessSelected, _useAccessGroupsCheckbox } = {}
	) => {
		if (!props.isTemp || !props.onTempGroupChange) return;
		const payload = {
			id_condominium: parseInt(selectedCondominio),
			licence: selectedLicenceObj,
			name: _name ?? name,
			start_date: _startDate ?? startDate,
			end_date: _endDate ?? endDate,
			start_hour: _startHour ?? startHour,
			end_hour: _endHour ?? endHour,
			description: _description ?? description,
			days_of_week_activated: [
				(_sunday ?? sunday) || false,
				(_mondayEnabled ?? mondayEnabled) || false,
				(_tuesdayEnabled ?? tuesdayEnabled) || false,
				(_wednesdayEnabled ?? wednesdayEnabled) || false,
				(_thursdayEnabled ?? thursdayEnabled) || false,
				(_friday ?? friday) || false,
				(_saturdayEnabled ?? saturdayEnabled) || false,
			],
			continuous_interval: (_continuousInterval ?? continuousInterval),
			keep_user: (_keepUser ?? keepUser),
			direction: parseInt(_direction ?? direction) || null,
			condoUsers: [],
			groupAccesses: (_accessSelected ?? accessSelected)?.map(a => ({
				id: a.relationId ?? null,
				moduleId: a.originalId,
				accessIndex: a.subIndex,
			})) || [],
			uuid: props.accessGroup?.uuid ?? uuidv4(),
			useAccessGroupsEnabled: (_useAccessGroupsCheckbox ?? useAccessGroupsCheckbox),
			isTempEnabled: true,
		};
		if (props.accessGroup?.id) {
			payload.id = props.accessGroup.id;
		}
		payload.name = payload.uuid;
		delete payload.condoUsers;
		props.onTempGroupChange(payload);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		props.isTemp, props.onTempGroupChange,
		name, startHour, endHour, startDate, endDate, description,
		mondayEnabled, tuesdayEnabled, wednesdayEnabled, thursdayEnabled,
		friday, saturdayEnabled, sunday, continuousInterval, keepUser, direction,
		accessSelected, useAccessGroupsCheckbox,
		selectedCondominio, selectedLicenceObj, props.accessGroup,
	]);

	const buildPayload = () => {
		let payload = {
			id_condominium: parseInt(selectedCondominio),
			licence: selectedLicenceObj,
			name: name,
			start_date: startDate,
			end_date: endDate,
			start_hour: startHour,
			end_hour: endHour,
			description: description,
			days_of_week_activated: [
				sunday || false,
				mondayEnabled || false,
				tuesdayEnabled || false,
				wednesdayEnabled || false,
				thursdayEnabled || false,
				friday || false,
				saturdayEnabled || false,
			],
			continuous_interval: continuousInterval,
			keep_user: keepUser,
			direction: parseInt(direction) || null,
			condoUsers: [],
			groupAccesses: accessSelected?.map(accessSelected => ({
				id: accessSelected.relationId ?? null,
				moduleId: accessSelected.originalId,
				accessIndex: accessSelected.subIndex,
			})) || [],
			uuid: uuidv4(),
			useAccessGroupsEnabled: useAccessGroupsCheckbox,
			isTempEnabled: props.isTemp ? true : false,
		};
		return payload;
	};

	function validateGroup() {
		if (!useAccessGroupsCheckbox && (!accessSelected || accessSelected.length === 0)) {
			NotificationManager.warning("Selecione pelo menos um acesso", "Atenção");
			return false;
		}
		return true;
	}

	async function handleSubmit(event) {
		event.preventDefault();
		if (props.type === "edicao") {
			handleEdit(event);
			return;
		}
		if (!validateGroup()) return;
		const payload = buildPayload();
		const response = await AccessGroupService.createAccessGroup(payload);
		if (response.data.id) navigate("../consulta-access-groups", {
			state: { refresh: true }
		});
	}

	async function handleEdit(event) {
		event.preventDefault();
		if (!validateGroup()) return;
		const payload = buildPayload();
		const response = await AccessGroupService.updateAccessGroup(
			props.accessGroup.id,
			payload
		);
		if (response.data.id) navigate("../consulta-access-groups");
	}

	async function handleDelete() {
		if (!props.accessGroup) return;
		await AccessGroupService.deleteAccessGroup(props.accessGroup.id);
		navigate("../consulta-access-groups", {
			state: { deletedId: props.accessGroup.id },
		});
	}

	const handleClickEditButton = () => {
		if (!props.accessGroup) return;
		navigate(`/access-groups/${props.accessGroup.uuid}`, { state: props.accessGroup });
	};

	const handleChangeDualList = (accessSelectedArray) => {
		setAccessSelected(accessSelectedArray);
		notifyTempGroup({ _accessSelected: accessSelectedArray });
	};

	return (
		<>
			{!props.isTemp && (
				<FormTitle
					type={props.type}
					icon={<LuUsers size={20} />}
					tituloConsulta={props.accessGroup?.name || null}
					tituloCadastro="Cadastro de Grupo de Acesso"
					backRoute={"/consulta-access-groups"}
				/>
			)}
			<Form
				handleSubmit={handleSubmit}
				type={props.type}
				handleClickEdit={handleClickEditButton}
				handleDelete={handleDelete}
				navigateCancelUrl="../consulta-access-groups"
				deleteButNotSynqed={props.accessGroup?.deletedAt ? true : false}
				hideSaveButtons={props.isTemp ? true : false}
			>
				<div className="container-initial-info">
					<div className="form-cond-container" style={{ width: useAccessGroupsCheckbox || props.type === "consulta" ? '100%' : '45%' }}>
						<div
							className={`container-name-direction ${props.type === "consulta" ? "consulta-mode" : ""
								}`}
						>
							<Input
								name="name"
								id="name"
								labelText="Nome *"
								value={name}
								onChange={({ target }) => { setName(target.value); notifyTempGroup({ _name: target.value }); }}
								disabled={props.type === "consulta"}
								required={true}
								hidden={props.isTemp}
								tooltipText="Nome do grupo de acesso"
								tooltipVisible={true}
							/>
							<Select
								labelText="Sentido Permitido"
								name="direction"
								id="direction"
								value={direction}
								onChange={({ target }) => { setDirection(target.value); notifyTempGroup({ _direction: target.value }); }}
								disabled={props.type === "consulta"}
								required={false}
								options={[
									{ value: 1, text: "Entrada e Saída" },
									{ value: 2, text: "Somente Entrada" },
									{ value: 3, text: "Somente Saída" },
									{ value: 4, text: "Entrada e Urna" },
									...(props.isTemp && useAccessGroupsCheckbox ? [{ value: 5, text: "Utilizar dos grupos" }] : [])
								]}
								tooltipText="Sentido permitido para o grupo de acesso"
								tooltipVisible={true}
							/>
							{props.isTemp && (
								<Checkbox
									labelText="Usar acessos de grupos"
									defaultChecked={useAccessGroupsCheckbox}
									onChange={({ target }) => {
										setUseAccessGroupsCheckbox(target.checked);
										notifyTempGroup({ _useAccessGroupsCheckbox: target.checked });
									}}
									required={props.isTemp}
									inputWidth="200px"
									minWidthLabel="120px"
									disabled={props.type === "consulta"}
								/>
							)}
						</div>

						<Input
							name="Descricao"
							id="descricao"
							labelText="Descrição"
							value={description}
							onChange={({ target }) => { setDescription(target.value); notifyTempGroup({ _description: target.value }); }}
							hidden={props.type === "consulta"}
							disabled={props.type === "consulta"}
							required={false}
							tooltipText="Descrição do grupo de acesso"
							tooltipVisible={true}
						/>

						<div className="date-time-box">
							<div className="date-time-container">
								<h3 className="box-title">Horário e Datas</h3>
								<div
									className={
										"date-time-grid" +
										(props.type === "consulta" ? " consulta-mode" : "")
									}
								>
									<Input
										type="date"
										labelText="Data de Início"
										value={startDate}
										onChange={({ target }) => { setStartDate(target.value); notifyTempGroup({ _startDate: target.value }); }}
										required={false}
										disabled={props.type === "consulta"}
										tooltipText="Data de início do grupo de acesso"
										tooltipVisible={true}
									/>
									<Input
										name="start_hour"
										id="start_hour"
										type="time"
										min="00:00"
										max="23:59"
										labelText="Hora Início"
										value={startHour}
										onChange={({ target }) => { setStartHour(target.value); notifyTempGroup({ _startHour: target.value }); }}
										disabled={props.type === "consulta"}
										required={false}
										tooltipText="Hora de início do grupo de acesso"
										tooltipVisible={true}
									/>
									<Input
										type="date"
										labelText="Data de Fim"
										value={endDate}
										onChange={({ target }) => { setEndDate(target.value); notifyTempGroup({ _endDate: target.value }); }}
										required={false}
										disabled={props.type === "consulta"}
										tooltipText="Data de fim do grupo de acesso"
										tooltipVisible={true}
									/>
									<Input
										name="end_hour"
										id="end_hour"
										type="time"
										min="00:00"
										max="23:59"
										labelText="Hora Fim"
										value={endHour}
										onChange={({ target }) => { setEndHour(target.value); notifyTempGroup({ _endHour: target.value }); }}
										disabled={props.type === "consulta"}
										required={false}
										tooltipText="Hora de fim do grupo de acesso"
										tooltipVisible={true}
									/>
								</div>
								<div className="container-interval-checkbox">
									<Checkbox
										labelText="Intervalo Contínuo"
										name="continuous_interval"
										id="continuous_interval"
										defaultChecked={continuousInterval}
										onChange={({ target }) => {
											setContinuousInterval(target.checked);
											notifyTempGroup({ _continuousInterval: target.checked });
										}}
										required={false}
										inputWidth="200px"
										minWidthLabel="120px"
										style={{ marginTop: '6px', marginBottom: '10px' }}
										disabled={props.type === "consulta"}
									/>
								</div>
							</div>

							{props.type !== "consulta" && (
								<>
									<div className="group-of-inputs-container week-container">
										<p className="group-of-inputs-title">Dias da semana ativos</p>
										<div className="checkbox-container">
											<Checkbox
												labelText="Domingo"
												defaultChecked={sunday}
												onChange={({ target }) => {
													setSundayEnabled(target.checked);
													notifyTempGroup({ _sunday: target.checked });
												}}
												required={false}
												inputWidth="200px"
												minWidthLabel="120px"
												disabled={props.type === "consulta"}
											></Checkbox>
											<Checkbox
												labelText="Segunda"
												defaultChecked={mondayEnabled}
												onChange={({ target }) => {
													setMondayEnabled(target.checked);
													notifyTempGroup({ _mondayEnabled: target.checked });
												}}
												required={false}
												inputWidth="200px"
												minWidthLabel="120px"
												disabled={props.type === "consulta"}
											></Checkbox>
											<Checkbox
												labelText="Terça"
												defaultChecked={tuesdayEnabled}
												onChange={({ target }) => {
													setTuesdayEnabled(target.checked);
													notifyTempGroup({ _tuesdayEnabled: target.checked });
												}}
												required={false}
												inputWidth="200px"
												minWidthLabel="120px"
												disabled={props.type === "consulta"}
											></Checkbox>
											<Checkbox
												labelText="Quarta"
												defaultChecked={wednesdayEnabled}
												onChange={({ target }) => {
													setWednesdayEnabled(target.checked);
													notifyTempGroup({ _wednesdayEnabled: target.checked });
												}}
												required={false}
												inputWidth="200px"
												minWidthLabel="120px"
												disabled={props.type === "consulta"}
											></Checkbox>
											<Checkbox
												labelText="Quinta"
												defaultChecked={thursdayEnabled}
												onChange={({ target }) => {
													setThursdayEnabled(target.checked);
													notifyTempGroup({ _thursdayEnabled: target.checked });
												}}
												required={false}
												inputWidth="200px"
												minWidthLabel="120px"
												disabled={props.type === "consulta"}
											></Checkbox>
											<Checkbox
												labelText="Sexta"
												defaultChecked={friday}
												onChange={({ target }) => {
													setFridayEnabled(target.checked);
													notifyTempGroup({ _friday: target.checked });
												}}
												required={false}
												inputWidth="200px"
												minWidthLabel="120px"
												disabled={props.type === "consulta"}
											></Checkbox>
											<Checkbox
												labelText="Sábado"
												defaultChecked={saturdayEnabled}
												onChange={({ target }) => {
													setSaturdayEnabled(target.checked);
													notifyTempGroup({ _saturdayEnabled: target.checked });
												}}
												required={false}
												inputWidth="200px"
												minWidthLabel="120px"
												disabled={props.type === "consulta"}
											></Checkbox>
										</div>
									</div>
									{props.isTemp && (
										<div className="container-interval-checkbox">
											<ToggleButton
												label="Manter usuário após o vencimento do acesso"
												name="keep_user"
												id="keep_user"
												defaultChecked={keepUser}
												onChange={({ target }) => {
													setKeepUser(target.checked);
													notifyTempGroup({ _keepUser: target.checked });
												}}
												required={false}
												disabled={props.type === "consulta"}
											/>
										</div>
									)}
								</>
							)}
						</div>
					</div>
					{props.type !== "consulta" && (
						<div className="container-dual-list-access-groups" style={{ display: useAccessGroupsCheckbox ? "none" : "flex" }}>
							<div className="form-cond-dual-list-container">
								<GroupAccessModulesDDL
									onChangeList={handleChangeDualList}
									accessGroup={props.isTemp ? initialAccessGroupRef.current : props.accessGroup}
								/>
							</div>
						</div>
					)}
				</div>
			</Form>
		</>
	);
}

export default FormAccessGroups;
